import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFindRecord, usePopulate } from 'api/APIHooks';
import { Spinner } from 'common/spinners';
import useInvoiceSpend from 'src/components/ServiceAuthorization/useInvoiceSpend';
import {
  useFindFileUploads,
  useFindProgram,
  useFindServiceAuthorization,
  useFindSocialCareCoverages,
} from '../../../../hooks';
import providedServiceInitialValues from '../../../../utils/providedServiceInitialValues';
import ProvidedServiceForm from './ProvidedServiceForm';

export const ProvidedServiceFormWrapper = ({
  contactId,
  providedService,
  serviceCase,
  isBillable,
  cancelFunction,
  formName,
  isEditing,
  paymentType,
  editOnFirstRender,
}) => {
  const programId = serviceCase?.program?.id;
  const serviceAuthorizationId = serviceCase?.service_authorization?.id;

  const { program, isSuccess: isProgramSuccess } = useFindProgram(programId);
  const feeScheduleProgram = program?.fee_schedule_program;
  const feeSchedule = feeScheduleProgram?.fee_schedule;

  const { socialCareCoverages, isSuccess: isSCCSuccess } = useFindSocialCareCoverages(contactId, feeSchedule?.id);

  const { serviceAuthorization, isSuccess: isSASuccess } = useFindServiceAuthorization(serviceAuthorizationId);
  const isServiceAuthReady = !serviceAuthorizationId || isSASuccess;
  const serviceAuthorizationPlanId = serviceAuthorization?.insurance?.plan?.id;
  const serviceAuthorizationPayerId = serviceAuthorization?.insurance?.plan?.payer?.id;

  const scrollRef = useRef();
  useEffect(() => {
    if (scrollRef.current && editOnFirstRender) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const [inferredPlanId, setInferredPlanId] = useState(null);
  const { data: inferredPlanData } = useFindRecord(
    'plan',
    inferredPlanId,
    { queryConfig: { enabled: !serviceAuthorizationId && !!inferredPlanId, placeholderData: undefined } },
  );
  const inferredPayerId = inferredPlanData?.data?.data?.payer?.id;
  const canonicalPlanId = serviceAuthorizationPlanId || inferredPlanId;
  const casePayerId = serviceAuthorizationPayerId || inferredPayerId;

  const serviceAuthorizationInvoiceInformation = useInvoiceSpend(serviceCase?.service_authorization?.id);

  const { isSuccess: isPCMSuccess } = usePopulate(
    'procedure_code_modifiers',
    'procedure_code_modifiers',
    providedService,
    { queryConfig: { placeholderData: undefined } },
  );
  const isPCMReady = isPCMSuccess || !providedService.procedure_code_modifiers?.length;

  const { fileUploads, isSuccess: isFileSuccess } = useFindFileUploads('provided_service', providedService.id);
  const isFileReady = !providedService.id || isFileSuccess;

  const capOrUnitInitialValue = paymentType === 'cost_based_reimbursement' ?
    feeScheduleProgram?.cap_information :
    feeScheduleProgram?.unit;
  const metafields = feeScheduleProgram?.metafields || [];
  const initialValues = providedServiceInitialValues({
    feeScheduleProgram,
    providedService,
    capOrUnitInitialValue,
    metafields,
    fileUploads,
  });

  const canInvoiceAboveRemainingAuthorizedAmount = feeScheduleProgram?.can_invoice_above_remaining_authorized_amount;
  const ignoreSocialCareExpiredDates = feeSchedule?.ignore_social_care_expired_dates || false;

  const isReady = isProgramSuccess && isSCCSuccess && isServiceAuthReady && isPCMReady && isFileReady;

  return (
    <div ref={scrollRef}>
      {!isReady ? (
        <Spinner />
      ) : (
        <ProvidedServiceForm
          casePayerId={casePayerId}
          feeScheduleProgram={feeScheduleProgram}
          fileUploads={fileUploads}
          planId={canonicalPlanId}
          providedService={providedService}
          socialInsurances={socialCareCoverages}
          serviceAuthorizationInvoiceInformation={serviceAuthorizationInvoiceInformation}
          serviceAuthorization={serviceAuthorization}
          setInferredPlanId={setInferredPlanId}
          isBillable={isBillable}
          cancelFunction={cancelFunction}
          formName={formName}
          isEditing={isEditing}
          paymentType={paymentType}
          serviceCase={serviceCase}
          metafields={metafields}
          initialValues={initialValues}
          canInvoiceAboveRemainingAuthorizedAmount={canInvoiceAboveRemainingAuthorizedAmount}
          ignoreSocialCareExpiredDates={ignoreSocialCareExpiredDates}
        />
      )}
    </div>
  );
};

ProvidedServiceFormWrapper.propTypes = {
  contactId: PropTypes.string.isRequired,
  providedService: PropTypes.object,
  serviceCase: PropTypes.object,
  isBillable: PropTypes.bool.isRequired,
  cancelFunction: PropTypes.func,
  formName: PropTypes.string.isRequired,
  isEditing: PropTypes.bool,
  paymentType: PropTypes.string,
  editOnFirstRender: PropTypes.bool,
};

ProvidedServiceFormWrapper.defaultProps = {
  providedService: {},
  serviceCase: {},
  cancelFunction: () => { },
  isEditing: false,
  paymentType: '',
  editOnFirstRender: false,
};

export default ProvidedServiceFormWrapper;
